"use client";

import { useEffect, useRef, useState } from "react";

import {
  extractFirstUrl,
  parseLinkPreview,
  youtubePreviewFromUrl,
  type LinkPreview,
} from "@/lib/feed/link-preview";

export function useLinkPreview(
  content: string,
  enabled: boolean,
  initialPreview: LinkPreview | null = null,
) {
  const [preview, setPreview] = useState<LinkPreview | null>(initialPreview);
  const [loading, setLoading] = useState(false);
  const dismissed = useRef(new Set<string>());
  const previewRef = useRef<LinkPreview | null>(initialPreview);
  previewRef.current = preview;

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const url = extractFirstUrl(content);
    if (!url || dismissed.current.has(url)) {
      return;
    }

    const youtube = youtubePreviewFromUrl(url);
    if (youtube && previewRef.current?.url !== url) {
      setPreview(youtube);
      previewRef.current = youtube;
    } else if (!youtube && previewRef.current?.url === url) {
      return;
    }

    const controller = new AbortController();
    const handle = window.setTimeout(() => {
      if (!youtube) {
        setLoading(true);
      }
      void fetch(`/api/link-preview?url=${encodeURIComponent(url)}`, {
        signal: controller.signal,
      })
        .then(async (response) => {
          const payload = await response.json();
          const parsed = parseLinkPreview(payload);
          if (parsed && !dismissed.current.has(url)) {
            setPreview({ ...parsed, url: youtube?.url ?? parsed.url });
          }
        })
        .catch(() => undefined)
        .finally(() => setLoading(false));
    }, youtube ? 150 : 700);

    return () => {
      window.clearTimeout(handle);
      controller.abort();
    };
  }, [content, enabled]);

  function dismiss() {
    const url = previewRef.current?.url ?? extractFirstUrl(content);
    if (url) {
      dismissed.current.add(url);
    }
    setPreview(null);
  }

  function reset(nextPreview: LinkPreview | null = null) {
    dismissed.current.clear();
    previewRef.current = nextPreview;
    setPreview(nextPreview);
    setLoading(false);
  }

  return { preview, loading, dismiss, reset, setPreview };
}
